import React, { useState } from 'react'
import { Box, IconButton, LinearProgress, Stack, Tooltip } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import EsimAPI from 'api/faber/EsimAPI'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import NormalText from 'common/Typography/NormalText'
import {format, isBefore} from 'date-fns'
import { formatExpiryText } from 'helpers/date'
import OrbitSession from 'hooks/useCookies'

import TerminateVoiceDialog from '../dialogs/TerminateVoicePlanDialog'

interface VoicePlan {
  CreatedAt: string;
  UpdatedAt: string;
  DeletedAt: string | null;
  createdBy: string;
  updatedBy: string;
  UserID: string;
  InitialOutgoing: number;
  Outgoing: number;
  InitialIncoming: number;
  Incoming: number;
  PaymentIntent: string;
  ExpiresAt: string;
  ID: string;
  CatalogID: string;
  catalog: {
    name: string;
    voiceInMins: number;
    validityInDays: number;
  }
}

const columns = (
  onTerminate: (row: VoicePlan) => void,
) => [
  {
    field: 'catalogName',
    headerName: 'Pack',
    sortable: false,
    minWidth: 300,
    renderCell: (params: { row: VoicePlan }) => (
      <NormalText variant="body1">
        {params.row.catalog?.name}
      </NormalText>
    )
  },
  {
    field: 'usage',
    headerName: 'Voice Remaining',
    sortable: false,
    minWidth: 150,
    renderCell: (params: { row: VoicePlan }) => {
      const {Outgoing, InitialOutgoing} = params.row;
      const unUsedInPercentage = Outgoing / InitialOutgoing * 100;
      const unUsedInMins = (Outgoing / 60).toFixed(2);
      const totalMins = (InitialOutgoing / 60).toFixed(2);
      return (
        <Stack direction="row" alignItems="center" spacing={1} width="100%">
          <LinearProgress
            variant="determinate"
            value={unUsedInPercentage}
            sx={{ width: 100, height: 4, borderRadius: 5 }}
            color="primary"
          />
          <NormalText variant="body2">
            {`${unUsedInMins}/${totalMins} mins`}
          </NormalText>
        </Stack>
      )
    }
  },
  {
    field: 'validity',
    headerName: 'Validity',
    sortable: false,
    minWidth: 150,
    renderCell: (params: { row: VoicePlan }) => (
      <NormalText variant="body2">
        {`${params.row?.catalog?.validityInDays} days`}
      </NormalText>
    )
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: false,
    minWidth: 200,
    renderCell: (params: { row: VoicePlan }) => (
      <NormalText variant="body2">
        { format(new Date(params.row.CreatedAt), 'dd MMM yyyy, HH:mm')}
      </NormalText>
    )
  },
  {
    field: 'expiresAt',
    headerName: 'Expires In',
    sortable: false,
    minWidth: 200,
    renderCell: (params: { row: VoicePlan }) => (
      <NormalText variant="body2">
        {formatExpiryText(new Date(params.row.ExpiresAt))}
      </NormalText>
    )
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: false,
    minWidth: 200,
    renderCell: (params: { row: VoicePlan }) => {
      const currentDate = new Date();
      const expiryDate = new Date(params.row.ExpiresAt);
      const isExpired = isBefore(expiryDate, currentDate);
      return (
        <NormalText variant="body2">
          {isExpired ? 'EXPIRED' : 'ACTIVE'}
        </NormalText>
      )
    }
  },
  {
    field: 'actions',
    headerName: 'Actions',
    sortable: false,
    minWidth: 150,
    renderCell: (params: { row: VoicePlan }) => {
      const handleTerminate = () => {
        onTerminate(params.row);
      };

      const isTerminated = params.row.DeletedAt !== null;

      return (
        <Box>
          {isTerminated ? (
            <NormalText>
              Terminated
            </NormalText>
          ) : (
            <Tooltip title={'Terminate voice pack'}>
              <IconButton
                color="primary"
                onClick={handleTerminate}
                sx={{
                  padding: 1,
                  backgroundColor: 'blue.50',
                  '&:hover': {
                    backgroundColor: 'blue.100',
                  },
                }}
                disabled={isTerminated}
              >
                <NormalText
                  sx={{
                    padding: 0.5,
                    border: 1,
                    borderStyle: 'solid',
                    borderRadius: 4,
                    borderColor: 'blue',
                    color: 'blue',
                  }}
                >
                  Terminate
                </NormalText>
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    },
  }
]

function VoicePacksCard({ userId }: { userId: string }) {
  const faberToken = OrbitSession.get('faberToken')
  const [openTerminateDialog, setOpenTerminateDialog] = useState<boolean>(false);
  const [selectedPack, setSelectedPack] = useState<VoicePlan | null>(null);

  const { data: voicePlans, isLoading } = useQuery(
    ['customer-voice-plans', userId],
    async () => {
      const response = await EsimAPI.getCustomerVoicePlans({
        userId,
        faberToken,
      });
      return response.data?.data;
    }
  );

  const onTerminate = (row: VoicePlan) => {
    setOpenTerminateDialog(true);
    setSelectedPack(row);
  }

  return (
    <>
      <NormalText isBold variant='h5'>
        Voice Packs
      </NormalText>

      <MUIDataGrid
        columns={columns(onTerminate)}
        rows={voicePlans || []}
        getRowId={(row: VoicePlan) => row.ID}
        loading={isLoading}
      />
      
      {selectedPack && (
        <TerminateVoiceDialog
          planId={selectedPack.ID}
          faberToken={faberToken}
          onClose={() => setOpenTerminateDialog(false)}
          open={openTerminateDialog}
        />
      )}
    </>
  )
}


export default VoicePacksCard
